import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { customFetch } from "@workspace/api-client-react";

type ThemeMode = "light" | "dark";

interface ThemeContextValue {
  theme: ThemeMode;
  setTheme: (mode: ThemeMode) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: "light",
  setTheme: () => {},
  toggleTheme: () => {},
});

export function useTheme() {
  return useContext(ThemeContext);
}

function applyTheme(mode: ThemeMode) {
  const root = document.documentElement;
  root.classList.toggle("dark", mode === "dark");
  root.classList.toggle("light", mode === "light");
  root.style.colorScheme = mode;
  root.setAttribute("data-theme", mode);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    const stored = localStorage.getItem("themeMode");
    if (stored === "dark" || stored === "light") return stored;
    // No stored preference — follow the OS setting
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });

  useLayoutEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Live-track OS preference changes; ignored once the user has manually toggled
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => {
      // Honour OS change only while there is no stored manual preference
      if (localStorage.getItem("themeMode")) return;
      setThemeState(e.matches ? "dark" : "light");
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  function setTheme(mode: ThemeMode) {
    setThemeState(mode);
    localStorage.setItem("themeMode", mode);
    applyTheme(mode);
  }

  function toggleTheme() {
    setTheme(theme === "light" ? "dark" : "light");
  }

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Place this component inside <ClerkProvider> after ClerkTokenSync.
 * Reconciles the DB-persisted theme preference with the local ThemeProvider.
 *
 * Auth surfaces:
 *  - Tenant employees  → GET/PUT /api/user/preferences (requireTenantAuth)
 *  - Vendor portal     → GET/PUT /api/vendor/preferences (requireVendorAuth)
 *
 * Lifecycle:
 *  - Tied to Clerk `userId`: re-fetches whenever the signed-in user changes.
 *    This handles login, logout, and multi-user switching within the same SPA run.
 *  - No timeout heuristic; instead we gate on `isSignedIn && userId` so we only
 *    fetch when Clerk has an active session (ClerkTokenSync is earlier in the
 *    component tree and its effect registers the auth getter before ours fires).
 */
export function ThemeDbSync() {
  const { theme, setTheme } = useTheme();
  const { userId, isSignedIn } = useAuth();

  // Which endpoint worked for this user — used by PUT to avoid re-probing
  const activeEndpointRef = useRef<"/api/user/preferences" | "/api/vendor/preferences" | null>(null);
  // Track whether we are past the first theme render so we can skip the initial PUT
  const isFirstThemeRender = useRef(true);

  // Load preference from DB whenever the signed-in user changes.
  // Resets endpoint + re-fetches on login, logout, and user switch.
  useEffect(() => {
    // Auth state not yet resolved — wait
    if (isSignedIn === undefined) return;

    // User signed out or changed — reset endpoint so PUT skips and re-fetch on next login
    activeEndpointRef.current = null;
    isFirstThemeRender.current = true;

    // Nothing to load when signed out
    if (!isSignedIn || !userId) return;

    let cancelled = false;

    (async () => {
      try {
        // Tenant employees
        const data = await customFetch<{ themeMode: string }>("/api/user/preferences");
        if (!cancelled && (data.themeMode === "light" || data.themeMode === "dark")) {
          activeEndpointRef.current = "/api/user/preferences";
          setTheme(data.themeMode as ThemeMode);
        }
      } catch {
        // Vendor accounts get 403 on the tenant endpoint — fall back
        try {
          const data = await customFetch<{ themeMode: string }>("/api/vendor/preferences");
          if (!cancelled && (data.themeMode === "light" || data.themeMode === "dark")) {
            activeEndpointRef.current = "/api/vendor/preferences";
            setTheme(data.themeMode as ThemeMode);
          }
        } catch {
          // Non-critical — localStorage value stays in effect
        }
      }
    })();

    return () => { cancelled = true; };
  }, [userId, isSignedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist to DB on every theme change — but skip the initialisation render
  // and any change that fires before the endpoint is resolved (user not logged in yet).
  useEffect(() => {
    if (isFirstThemeRender.current) { isFirstThemeRender.current = false; return; }
    const endpoint = activeEndpointRef.current;
    if (!endpoint) return;
    customFetch(endpoint, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ themeMode: theme }),
    }).catch(() => {});
  }, [theme]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}
